import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalData } from "../../../../../../contexts/GlobalDataProvider";
import query from "../../../../../../server/constants.api";
import useFetch from "../../../../../../server/useFetch";
import { AUTH_USER_POOL_ID, REACT_APP_PROJECT_NAME } from "../../../../../../utils/config/configs";
import { handleNotificationWithAction } from "../../../../../../utils/config/notificationHandler";
import { sortTenantsByHierarchy } from "../../../../../../utils/hooks/sortUtils";
import { EditPopupConfigurations } from "../../EditPopupConfigurations";
import useUsersCreateEditModal from "./useUsersCreateEditModal/useUsersCreateEditModal";

const UsersCreateEditModal = ({ isModalOpen, handleCloseModal, isToEdit, selectedRecord, updateUserTableList }) => {
    const { t } = useTranslation();
    const [bodyToPost, setBodyToPost] = useState(null);

    const { subTenants, isFetchingSubTenants } = useGlobalData();

    const {
        data: addOrEditUserData,
        isFetching: addOrEditUserIsFetching,
        error: addOrEditUserError,
        isSuccess: addOrEditUserIsSuccess,
        isError: addOrEditUserIsError,
        refetch: refetchAddOrEditUser,
        remove: removeAddOrEditUser
    } = useFetch(
        "/users",
        'editUser',
        bodyToPost,
        false,
        isToEdit ? 'PUT' : 'POST',
        query.middlewareURL
    );

    const {
        data: projectConfigurationsData,
        isFetching: projectConfigurationsIsFetching,
        remove: removeProjectConfigurations,
    } = useFetch(
        query.path.projectConfigurations,
        "getProjectConfigurations",
        {
            "project-name": REACT_APP_PROJECT_NAME
        },
        true,
        'GET',
        query.middlewareURL
    );

    /**
     * Effect to clean up the component when it unmounts
     */
    useEffect(() => {
        return () => {
            removeProjectConfigurations();
        }
    }, []);

    /**
     * Effect to create or edit a user when bodyToPost is set.
     */
    useEffect(() => {
        if (bodyToPost) {
            refetchAddOrEditUser();
        }
    }, [bodyToPost]);

    /**
     * Effect to handle notifications based on the status of adding or editing a user.
     * It executes actions based on the success or error of the operation,
     */
    useEffect(() => {
        const successMessage = isToEdit ? "notification.success_edit_user" : "notification.success_create_user";
        const errorMessage = isToEdit ? "notification.error_edit_user" : "notification.error_create_threshold";
        handleNotificationWithAction(addOrEditUserIsFetching, addOrEditUserIsSuccess, addOrEditUserIsError, addOrEditUserError,
            successMessage, errorMessage, t,
            onAddOrEditUserSuccess, onAddOrEditUserSuccessOrError);

    }, [addOrEditUserIsError, addOrEditUserIsSuccess, addOrEditUserIsFetching]);

    /**
     * Handles the success scenario of adding or editing a user.
     * Closes the modal, removes the user from the add/edit state,
     * and updates the user table list if user data is available.
     *
     * @function
     * @returns {void}
     */
    const onAddOrEditUserSuccess = () => {
        handleCloseModal();
        removeAddOrEditUser();
        if (addOrEditUserData) {
            updateUserTableList(addOrEditUserData);
        }
    }

    /**
     * Handles the success or error response after adding or editing a user.
     * Resets the body to post to null.
     *
     * @function
     * @returns {void}
     */
    const onAddOrEditUserSuccessOrError = () => {
        setBodyToPost(null);
    }

    /**
     * Handles the confirmation action of the modal.
     * Prepares the data to be posted by transforming the input data
     * and setting specific properties such as mfa_active, tenant,
     * zones, tags, and userpool_id.
     *
     * @param {Object} data - The data object containing user configuration details.
     * @param {Array} data.tenant - An array of tenant objects.
     * @param {Array} data.zones - An array of zone objects.
     * @param {Array} data.tags - An array of tag objects.
     */
    const handleOkModal = (data) => {

        const copyData = {
            ...data,
            mfa_active: false,
            tenant: JSON.stringify(sortTenantsByHierarchy(data.tenant ? data.tenant.map(t => t.value) : [])),
            zones: JSON.stringify(data.zones ? data.zones.map(t => t.value) : []),
            tags: JSON.stringify(data.tags ? data.tags.map(t => t.value) : []),
            userpool_id: AUTH_USER_POOL_ID
        };

        setBodyToPost(copyData);
    }

    /**
     * Serializes a selected record for display in a modal based on the provided table configuration.
     *
     * @param {Object} selectedRecord - The record to be serialized.
     * @param {Array<Object>} tableConfiguration - The configuration for the table, containing field definitions.
     * @returns {Array<Object>} An array of serialized objects containing the keys, values, and additional field configurations.
     */
    const serializeRecordForModal = (selectedRecord, tableConfiguration) => {
        const keysToKeep = tableConfiguration.map(x => x.dataIndex);

        const result = Object.entries(selectedRecord)
            .filter(([key]) => keysToKeep.includes(key))
            .map(([key, value]) => {
                const fieldConfig = tableConfiguration.find(config => config.dataIndex === key);
                return {
                    key,
                    value,
                    ...fieldConfig,
                    options: returnOptions(key),
                    name: fieldConfig.title || key,
                };
            });

        return result
            .sort((a, b) => keysToKeep.indexOf(a.key) - keysToKeep.indexOf(b.key));
    };

    /**
     * Returns an array of options based on the provided key.
     *
     * @param {string} key - The key to determine which options to return. 
     *                       Possible values are 'zones', 'tags', and 'tenant'.
     * @returns {Array<{ label: string, value: string }> | null} - 
     *          An array of objects containing label and value properties 
     *          corresponding to the specified key, or null if the key is not recognized.
     */
    const returnOptions = (key) => {
        switch (key) {
            case 'zones':
                return projectConfigurationsData?.Zones?.map(x => { return { label: x, value: x } }) || [];
            case 'tags':
                return projectConfigurationsData?.Tags?.map(x => { return { label: x, value: x } }) || [];
            case 'tenant':
                return sortTenantsByHierarchy(subTenants).map(x => { return { label: x, value: x } });
            default:
                return null;
        }
    }

    /**
     * Custom hook to manage the modal state and configuration for creating or editing users.
     */
    const usersCreateEditModal = useUsersCreateEditModal({
        isFetchingSubTenants,
        projectConfigurationsIsFetching,
        isEditMode: isToEdit,
    });

    return (
        <EditPopupConfigurations
            openModal={isModalOpen}
            closeModal={handleCloseModal}
            isToEdit={isToEdit}
            onOkModal={(data) => handleOkModal(data)}
            customWidth={700}
            selectedRecord={selectedRecord ? serializeRecordForModal(selectedRecord, usersCreateEditModal) : null}
            loading={addOrEditUserIsFetching}
        />
    )
}

export default UsersCreateEditModal;